import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { Briefcase, Loader2, RefreshCw, X, Plus, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobProgressRow = ({ job, onClick, onHide }) => {
  const pct = job.target > 0 ? Math.min(100, Math.round((job.delivered / job.target) * 100)) : 0;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      data-testid={`jobs-in-progress-row-${job.id}`}
      className="w-full text-left border rounded-lg px-4 py-3 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
    >
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <span className="font-medium text-gray-900 text-sm truncate">
          {job.title}
          {job.is_split_parent && (
            <span className="ml-2 text-[11px] text-purple-600 font-semibold">
              {job.children_in_progress} map{job.children_in_progress === 1 ? '' : 's'} active
            </span>
          )}
        </span>
        <span className="flex items-center gap-2 whitespace-nowrap">
          <span className="text-xs font-semibold text-gray-700" data-testid={`jobs-in-progress-pct-${job.id}`}>
            {job.target > 0
              ? `${job.delivered.toLocaleString()}/${job.target.toLocaleString()} (${pct}%)`
              : `${job.delivered.toLocaleString()} delivered`}
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onHide(job); }}
            className="text-gray-300 hover:text-red-500 transition-colors"
            title="Remove this job from the Jobs in Progress list"
            data-testid={`jobs-in-progress-hide-${job.id}`}
          >
            <X size={14} />
          </button>
        </span>
      </div>
      <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export const JobsInProgress = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/jobs-in-progress`, getAuthHeader());
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const hideJob = async (job) => {
    if (!window.confirm(`Remove "${job.title}" from the Jobs in Progress list?`)) return;
    try {
      await axios.put(`${API}/admin/jobs/${job.id}/hide-from-progress`, {}, getAuthHeader());
      toast.success(`"${job.title}" removed from Jobs in Progress`);
      fetchJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove job');
    }
  };

  // Add Job dropdown — searchable list of jobs not currently displayed
  const [addOpen, setAddOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [availLoading, setAvailLoading] = useState(false);
  const [addSearch, setAddSearch] = useState('');

  const toggleAddMenu = async () => {
    const next = !addOpen;
    setAddOpen(next);
    if (next) {
      setAddSearch('');
      setAvailLoading(true);
      try {
        const res = await axios.get(`${API}/admin/jobs-in-progress/available`, getAuthHeader());
        setAvailable(res.data.jobs || []);
      } catch {
        toast.error('Failed to load jobs');
      } finally {
        setAvailLoading(false);
      }
    }
  };

  const addJob = async (job) => {
    try {
      await axios.put(`${API}/admin/jobs/${job.id}/show-in-progress`, {}, getAuthHeader());
      toast.success(`"${job.title}" added to Jobs in Progress`);
      setAddOpen(false);
      fetchJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add job');
    }
  };

  const filteredAvailable = available.filter((j) =>
    (j.title || '').toLowerCase().includes(addSearch.toLowerCase())
  );

  return (
    <Card className="p-6" data-testid="jobs-in-progress-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Briefcase size={20} className="text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Jobs in Progress</h2>
          {data && (
            <span className="text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-2 py-0.5" data-testid="jobs-in-progress-count">
              {data.count}
            </span>
          )}
          <div className="relative ml-2">
            <button
              type="button"
              onClick={toggleAddMenu}
              className="flex items-center gap-1 text-xs font-medium text-blue-600 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50 transition-colors"
              data-testid="jobs-in-progress-add-btn"
              title="Add a job to this list"
            >
              <Plus size={13} /> Add Job
            </button>
            {addOpen && (
              <div className="absolute left-0 top-8 z-30 w-80 bg-white border rounded-lg shadow-lg p-2" data-testid="jobs-in-progress-add-menu">
                <div className="flex items-center gap-1.5 border rounded-md px-2 py-1.5 mb-2">
                  <Search size={13} className="text-gray-400" />
                  <input
                    type="text"
                    autoFocus
                    placeholder="Search jobs by name…"
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    className="flex-1 text-sm outline-none"
                    data-testid="jobs-in-progress-add-search"
                  />
                </div>
                {availLoading ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400 py-3 justify-center">
                    <Loader2 className="animate-spin" size={13} /> Loading jobs…
                  </div>
                ) : filteredAvailable.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3 text-center">
                    {available.length === 0 ? 'No jobs available to add.' : 'No jobs match your search.'}
                  </p>
                ) : (
                  <div className="max-h-56 overflow-y-auto">
                    {filteredAvailable.map((j) => (
                      <button
                        key={j.id}
                        type="button"
                        onClick={() => addJob(j)}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-blue-50 flex items-center justify-between gap-2"
                        data-testid={`jobs-in-progress-add-option-${j.id}`}
                      >
                        <span className="text-sm text-gray-800 truncate">{j.title}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${j.hidden ? 'bg-gray-100 text-gray-500' : 'bg-amber-50 text-amber-600'}`}>
                          {j.hidden ? 'hidden' : j.status.replace('_', ' ')}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <button type="button" onClick={() => { setLoading(true); fetchJobs(); }} data-testid="jobs-in-progress-refresh-btn"
          className="text-gray-400 hover:text-gray-600" title="Refresh">
          <RefreshCw size={15} />
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center">
          <Loader2 className="animate-spin" size={16} /> Loading jobs…
        </div>
      ) : !data || data.jobs.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm flex flex-col items-center gap-2">
          <Briefcase size={22} />
          No jobs currently in progress.
        </div>
      ) : (
        <div className="space-y-2">
          {data.jobs.map((j) => (
            <JobProgressRow key={j.id} job={j} onClick={() => navigate(`/admin/client-report/${j.id}`)} onHide={hideJob} />
          ))}
        </div>
      )}
    </Card>
  );
};

export default JobsInProgress;
